"use client";

import {
  Box,
  Code,
  Flex,
  Grid,
  Link,
  Separator,
  Text,
  VisuallyHidden,
} from "@radix-ui/themes";
import * as React from "react";
import { type ReactNode } from "react";
import * as Otp from "./otp-input.js";
import { Dialog, Button } from "./elements.js";
import {
  CreateTotpFactorResponse,
  useCreateTotpFactor,
  useVerifyTotpFactor,
} from "../api/endpoint.js";
import { useElevatedAccessToken } from "../api/api-provider.js";
import { Form } from "radix-ui";
import { Marker } from "./marker.js";
import { CopyButton } from "./copy-button.js";
import { useSecuritySettings } from "./use-security-settings.js";
import { ElevatedAccess } from "./elevated-access.js";
import { SaveButton } from "./save-button.js";
import { useDialogClose } from "./use-dialog-close.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";

interface AddMfaDialogProps extends Dialog.RootProps {
  children?: ReactNode;
  onSuccess?: () => void;
}

export function AddMfaDialog({
  children,
  onSuccess,
  ...props
}: AddMfaDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [manuallyTriggered, setManuallyTriggered] = React.useState(false);
  const { elevatedAccess } = useElevatedAccessToken();
  const createAuthFactor = useCreateTotpFactor();

  const handleVerified = async () => {
    await createAuthFactor.mutateAsync(undefined);
  };

  const onTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (elevatedAccess && !createAuthFactor.data) {
      event.preventDefault();
      setManuallyTriggered(true);
      createAuthFactor.mutate(undefined, {
        onSuccess: () => {
          setOpen(true);
        },
      });
    } else {
      setManuallyTriggered(false);
    }
  };

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Dialog.Root
      {...props}
      open={props.open || open}
      onOpenChange={props.onOpenChange || setOpen}
    >
      <Dialog.Trigger
        onClick={onTriggerClick}
        // @ts-ignore I've to find a way to pass the loading state to the trigger
        loading={manuallyTriggered && createAuthFactor.isPending}
      >
        {children}
      </Dialog.Trigger>

      <Dialog.Content maxWidth="480px">
        <ElevatedAccess onVerified={handleVerified}>
          <Content onClose={handleClose} totpFactor={createAuthFactor.data} />
        </ElevatedAccess>
      </Dialog.Content>
    </Dialog.Root>
  );
}

interface ContentProps {
  totpFactor?: CreateTotpFactorResponse;
  onClose?: () => void;
}

function Content({ onClose, totpFactor }: ContentProps) {
  const securitySettings = useSecuritySettings();
  const translate = useTranslation();

  const [customError, setCustomError] = React.useState<string | null>(null);
  const totp =
    totpFactor?.authenticationFactor.totp != null &&
    "qr_code" in totpFactor.authenticationFactor.totp
      ? totpFactor.authenticationFactor.totp
      : undefined;

  const verifyTotp = useVerifyTotpFactor({
    mutation: {
      onSuccess: ({ success }) => {
        if (!success) {
          setCustomError("Invalid passcode");
        }
      },
    },
  });

  const serverError = verifyTotp.error || customError;
  const isSuccess = verifyTotp.isSuccess && !serverError;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCustomError(null);

    const formData = new FormData(event.currentTarget);
    const otpCode = formData.get("otp-code")?.toString();

    verifyTotp.mutate({
      data: {
        authenticationChallengeId: totpFactor?.authenticationChallenge.id ?? "",
        code: otpCode ?? "",
      },
    });
  };

  useDialogClose(isSuccess, () => {
    securitySettings.update("Mfa", true);
  });

  return (
    <>
      <Dialog.Title mb="5">
        <Translation
          defaultMessage="Set up an authenticator app"
          id="Eu+kuO"
          description="Title for setting up two-factor authentication"
        />
      </Dialog.Title>

      <Form.Root onSubmit={handleSubmit}>
        <Grid columns="auto 1fr" rows="repeat(4, auto)" gapX="3" gapY="1">
          <Grid
            rows="subgrid"
            gridRow="span 4"
            style={{ placeItems: "center" }}
          >
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <Marker>1</Marker>
            <Separator
              orientation="vertical"
              size="4"
              style={{ width: "2px" }}
            />
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <Marker>2</Marker>
            <div />
          </Grid>

          <Grid rows="subgrid" gridRow="span 4">
            <Text as="p" size="3" weight="bold">
              <Translation
                defaultMessage="Scan the QR code"
                id="fTSp5u"
                description="Heading for the QR code scanning step in MFA setup"
              />
            </Text>
            <Flex direction="column" gap="1">
              <Text as="p" size="2">
                <Translation
                  defaultMessage="Use an authenticator app like {onep}, {google}, {authy}, or {microsoft} to scan the QR code below."
                  id="8xwdwI"
                  description="Instructions for scanning the QR code with authenticator apps"
                  values={{
                    onep: (
                      <Link
                        href="https://1password.com/"
                        rel="noopener noreferrer"
                        target="_blank"
                        // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                      >
                        1Password
                      </Link>
                    ),
                    google: (
                      <Link
                        href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                        rel="noopener noreferrer"
                        target="_blank"
                        // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                      >
                        Google Authenticator
                      </Link>
                    ),
                    authy: (
                      <Link
                        href="https://authy.com/"
                        rel="noopener noreferrer"
                        target="_blank"
                        // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                      >
                        Authy
                      </Link>
                    ),
                    microsoft: (
                      <Link
                        href="https://www.microsoft.com/en-gb/security/mobile-authenticator-app"
                        rel="noopener noreferrer"
                        target="_blank"
                        // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                      >
                        Microsoft Authenticator
                      </Link>
                    ),
                  }}
                />
              </Text>
              <Flex align="center" gap="5" my="5">
                <Grid
                  position="relative"
                  width="160px"
                  height="160px"
                  p="2"
                  style={{
                    border: "1px solid var(--gray-7)",
                    borderRadius: "var(--radius-4)",
                    background: "var(--gray-2)",
                    overflow: "hidden",
                  }}
                >
                  {!!totp?.qr_code && (
                    <Box asChild width="100%" height="auto">
                      <img
                        alt={translate({
                          defaultMessage: "Scan this QR code to enroll",
                          description: "Alt text for QR code image",
                          id: "wpbqFJ",
                        })}
                        aria-describedby="secret-note"
                        height="160"
                        src={totp.qr_code}
                        style={{ userSelect: "none", background: "white" }}
                        width="160"
                      />
                    </Box>
                  )}
                </Grid>

                <Flex direction="column" gap="1" align="start">
                  <Text color="gray" id="secret-note" size="2">
                    <Translation
                      defaultMessage="Can't scan the code?"
                      id="6/cdcS"
                      description="Question asking if user is unable to scan the QR code"
                    />
                  </Text>

                  <SecretDialog
                    setupKey={totp && "secret" in totp ? totp.secret : ""}
                  >
                    <Button
                      variant="secondary"
                      size="1"
                      disabled={verifyTotp.isPending || isSuccess}
                    >
                      <Translation
                        defaultMessage="View setup key"
                        id="q3eZjH"
                        description="Button text to view the manual setup key"
                      />
                    </Button>
                  </SecretDialog>
                </Flex>
              </Flex>
            </Flex>
            <Text as="p" size="3" weight="bold">
              <Translation
                defaultMessage="Get verification passcode"
                id="1kRd2b"
                description="Heading for entering the verification code step"
              />
            </Text>
            <Flex direction="column" gap="4">
              <Text as="p" size="2">
                <Translation
                  defaultMessage="Enter the 6-digit passcode from your authenticator app."
                  id="4/66bS"
                  description="Instructions for entering the verification passcode"
                />
              </Text>

              <Flex direction="column" gap="2">
                <Otp.Root
                  autoSubmit
                  gap="3"
                  justify="start"
                  name="otp-code"
                  rows="48px"
                  columns="repeat(6, 48px)"
                  readOnly={verifyTotp.isPending || isSuccess}
                >
                  <Otp.Input required autoFocus autoComplete="off" />
                  <Otp.Input required />
                  <Otp.Input required />
                  <Otp.Input required />
                  <Otp.Input required />
                  <Otp.Input required />
                </Otp.Root>

                {serverError && (
                  <Text size="2" color="red">
                    {getMutationErrorMessage(serverError)}
                  </Text>
                )}
              </Flex>
            </Flex>
          </Grid>
        </Grid>

        <Flex mt="5" gap="3" justify="end">
          <Dialog.Close>
            <Button
              variant="secondary"
              disabled={verifyTotp.isPending || isSuccess}
            >
              <Translation
                defaultMessage="Cancel"
                id="84pAvU"
                description="Button text to cancel MFA setup"
              />
            </Button>
          </Dialog.Close>

          <SaveButton
            type="submit"
            loading={verifyTotp.isPending}
            done={isSuccess}
            onDone={onClose}
          >
            <Translation
              defaultMessage="Confirm"
              id="WHthZi"
              description="Button text to confirm MFA setup"
            />
          </SaveButton>
        </Flex>
      </Form.Root>

      {/* mirror errors in a live region */}
      <VisuallyHidden asChild>
        <section aria-live="polite">
          {getMutationErrorMessage(serverError)}
        </section>
      </VisuallyHidden>
    </>
  );
}

interface SecretDialogProps extends React.PropsWithChildren {
  setupKey: string;
}

function SecretDialog({ children, setupKey }: SecretDialogProps) {
  return (
    <Dialog.Root>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Content
        maxWidth="90vw"
        size={{ initial: "3", sm: "4" }}
        minWidth="300px"
        width="fit-content"
      >
        <Dialog.Title size="2" weight="regular">
          <Translation
            defaultMessage="Your setup key"
            id="LleqNP"
            description="Title for dialog showing the manual setup key"
          />
        </Dialog.Title>
        <Dialog.Description>
          <Text size="6">
            <Code variant="ghost" style={{ letterSpacing: "0.15rem" }}>
              {setupKey}
            </Code>
          </Text>
        </Dialog.Description>

        <Flex align="center" gap="3" justify="end" mt="5">
          <Dialog.Close>
            <CopyButton value={setupKey}>
              <Translation
                defaultMessage="Copy and close"
                id="EyjGir"
                description="Button text to copy setup key and close dialog"
              />
            </CopyButton>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function getMutationErrorMessage(error: unknown) {
  let message = "Something went wrong, please try again";

  if (typeof error === "string") {
    message = error;
  }

  if (error instanceof Error) {
    message = error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    message = error.message;
  }

  if (message === "Invalid passcode") {
    message = "Invalid passcode, please try again";
  }

  return message;
}
